// One shared tooltip layer for the whole app. A single delegated listener on document.body
// drives every `.info-dot` (a circled-i affordance) and any element carrying `data-tip`,
// wherever it lives — results panel, parameter controls, mesh UI, etc. Pure DOM, zero deps.
//
// Triggers: hover (mouseover/out), keyboard focus (focusin/out), AND click/tap (toggle) — so
// it works with a mouse, a keyboard, and on touch devices where there is no hover. Previously
// the listener was scoped to the results panel only, so parameter info-dots did nothing; this
// module fixes that by installing once, globally.

let tipEl: HTMLDivElement | null = null;
let pinnedTarget: HTMLElement | null = null; // the dot currently "clicked open"
let installed = false;

function tooltipEl(): HTMLDivElement {
  if (!tipEl) {
    tipEl = document.createElement("div");
    tipEl.className = "ls-tooltip";
    tipEl.setAttribute("role", "tooltip");
    document.body.appendChild(tipEl);
  }
  return tipEl;
}

/** Show arbitrary HTML at a viewport point (also used by the histogram hover). */
export function showTip(html: string, clientX: number, clientY: number): void {
  const el = tooltipEl();
  el.innerHTML = html;
  el.style.display = "block";
  const pad = 8;
  const rect = el.getBoundingClientRect();
  let x = clientX + 12;
  let y = clientY - rect.height - 12;
  if (x + rect.width + pad > window.innerWidth) x = clientX - rect.width - 12;
  if (y < pad) y = clientY + 16;
  el.style.left = `${Math.max(pad, x)}px`;
  el.style.top = `${y}px`;
}

export function hideTip(): void {
  if (tipEl) tipEl.style.display = "none";
  pinnedTarget = null;
}

function tipFor(el: HTMLElement): string | null {
  return el.getAttribute("data-tip");
}

/** Show the tooltip anchored above an element (used for info-dots). */
function showForElement(dot: HTMLElement): void {
  const tip = tipFor(dot);
  if (!tip) return;
  const rect = dot.getBoundingClientRect();
  showTip(tip, rect.left + rect.width / 2, rect.top);
}

// Find the nearest ancestor (incl. self) that is an info-dot carrying a tip.
function closestDot(node: EventTarget | null): HTMLElement | null {
  let el = node as HTMLElement | null;
  while (el && el !== document.body) {
    if (el.classList?.contains("info-dot") && el.hasAttribute("data-tip")) return el;
    el = el.parentElement;
  }
  return null;
}

/** Install the global tooltip behaviour once. Idempotent — safe to call on every page. */
export function installTooltips(): void {
  if (installed) return;
  installed = true;

  // Hover.
  document.body.addEventListener("mouseover", (e) => {
    const dot = closestDot(e.target);
    if (dot && dot !== pinnedTarget) showForElement(dot);
  });
  document.body.addEventListener("mouseout", (e) => {
    // Don't hide a click-pinned tip on mouse-out; let the next click / outside-click close it.
    if (pinnedTarget) return;
    if (closestDot(e.target)) hideTip();
  });

  // Keyboard focus.
  document.body.addEventListener("focusin", (e) => {
    const dot = closestDot(e.target);
    if (dot) showForElement(dot);
  });
  document.body.addEventListener("focusout", (e) => {
    if (closestDot(e.target)) hideTip();
  });

  // Click / tap: toggle a "pinned" tip (so it works without hover, e.g. touch).
  document.body.addEventListener("click", (e) => {
    const dot = closestDot(e.target);
    if (dot) {
      e.preventDefault();
      if (pinnedTarget === dot) {
        hideTip();
      } else {
        pinnedTarget = dot;
        showForElement(dot);
      }
      return;
    }
    // Click anywhere else closes a pinned tip.
    if (pinnedTarget) hideTip();
  });

  // Esc closes; scroll/resize repositions away (simplest: hide).
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") hideTip();
  });
  window.addEventListener("scroll", () => hideTip(), true);
}

// The circled-"i" SVG used for info affordances — shared so results + params + mesh match.
export const INFO_SVG =
  '<svg class="info-svg" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">' +
  '<circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2"/>' +
  '<circle cx="8" cy="4.6" r="0.95" fill="currentColor"/>' +
  '<rect x="7.15" y="6.7" width="1.7" height="5" rx="0.85" fill="currentColor"/>' +
  "</svg>";

/** Build an info-dot span carrying its tip. `tip` must already be HTML-safe if it contains
 *  markup; callers pass escaped text. */
export function infoDot(tip: string): string {
  return `<span class="info-dot" tabindex="0" data-tip="${tip}" role="img" aria-label="More info">${INFO_SVG}</span>`;
}
